/**
 * Affiliate Configuration System
 * 
 * Manages affiliate IDs, tracking parameters, and program details
 * Supports both direct affiliate programs and affiliate networks
 */

export type AffiliateProvider =
  // Job Boards
  | 'ziprecruiter'
  | 'indeed'
  | 'linkedin'
  | 'monster'
  | 'glassdoor'
  | 'careerbuilder'
  | 'simplyhired'
  // Education/Training
  | 'coursera'
  | 'udemy'
  | 'skillshare'
  | 'edx'
  | 'pluralsight'
  | 'linkedin-learning'
  // Resume Services
  | 'topresume'
  | 'resume-io'
  | 'zety'
  | 'resume-genius'
  // Career Resources
  | 'amazon';

export type AffiliateType = 'job-search' | 'training' | 'resume' | 'resource';

export interface AffiliateConfig {
  provider: AffiliateProvider;
  affiliateId?: string;
  networkId?: string; // For ShareASale, CJ, etc.
  trackingParams: Record<string, string>;
  baseUrl: string;
  cookieDuration?: number; // in days
  enabled: boolean;
}

/**
 * Get affiliate ID from environment variables
 */
const getAffiliateId = (provider: string): string | undefined => {
  const envKey = `VITE_AFFILIATE_${provider.toUpperCase().replace(/-/g, '_')}_ID`;
  return import.meta.env[envKey];
};

/**
 * Get network ID from environment variables
 */
const getNetworkId = (network: string): string | undefined => {
  const envKey = `VITE_AFFILIATE_${network.toUpperCase()}_ID`;
  return import.meta.env[envKey];
};

/**
 * Affiliate program configurations
 */
export const affiliateConfigs: Record<AffiliateProvider, AffiliateConfig> = {
  // Job Boards
  ziprecruiter: {
    provider: 'ziprecruiter',
    affiliateId: getAffiliateId('ziprecruiter'),
    baseUrl: 'https://www.ziprecruiter.com/jobs',
    trackingParams: {},
    enabled: !!getAffiliateId('ziprecruiter'),
  },
  indeed: {
    provider: 'indeed',
    affiliateId: getAffiliateId('indeed'),
    baseUrl: 'https://www.indeed.com/jobs',
    trackingParams: {},
    enabled: !!getAffiliateId('indeed'),
  },
  linkedin: {
    provider: 'linkedin',
    affiliateId: getAffiliateId('linkedin'),
    baseUrl: 'https://www.linkedin.com/jobs/search',
    trackingParams: {},
    enabled: !!getAffiliateId('linkedin'),
  },
  monster: {
    provider: 'monster',
    networkId: getNetworkId('shareasale'), // Monster typically via ShareASale
    baseUrl: 'https://www.monster.com/jobs',
    trackingParams: {},
    enabled: !!getNetworkId('shareasale'),
  },
  glassdoor: {
    provider: 'glassdoor',
    affiliateId: getAffiliateId('glassdoor'),
    baseUrl: 'https://www.glassdoor.com/Job',
    trackingParams: {},
    enabled: !!getAffiliateId('glassdoor'),
  },
  careerbuilder: {
    provider: 'careerbuilder',
    networkId: getNetworkId('shareasale'), // CareerBuilder via ShareASale
    baseUrl: 'https://www.careerbuilder.com/jobs',
    trackingParams: {},
    enabled: !!getNetworkId('shareasale'),
  },
  simplyhired: {
    provider: 'simplyhired',
    affiliateId: getAffiliateId('simplyhired'),
    baseUrl: 'https://www.simplyhired.com/search',
    trackingParams: {},
    enabled: !!getAffiliateId('simplyhired'),
  },
  // Education/Training
  coursera: {
    provider: 'coursera',
    affiliateId: getAffiliateId('coursera'),
    baseUrl: 'https://www.coursera.org/search',
    trackingParams: {},
    cookieDuration: 14, // Impact Engagement attribution window
    enabled: !!getAffiliateId('coursera'),
  },
  udemy: {
    provider: 'udemy',
    affiliateId: getAffiliateId('udemy'),
    baseUrl: 'https://www.udemy.com/courses/search',
    trackingParams: {},
    cookieDuration: 30, // Impact Engagement attribution window
    enabled: !!getAffiliateId('udemy'),
  },
  skillshare: {
    provider: 'skillshare',
    affiliateId: getAffiliateId('skillshare'),
    baseUrl: 'https://www.skillshare.com',
    trackingParams: {},
    cookieDuration: 30, // Impact Engagement click attribution window
    enabled: !!getAffiliateId('skillshare'),
  },
  edx: {
    provider: 'edx',
    affiliateId: getAffiliateId('edx'),
    baseUrl: 'https://www.edx.org/search',
    trackingParams: {},
    cookieDuration: 30,
    enabled: !!getAffiliateId('edx'),
  },
  pluralsight: {
    provider: 'pluralsight',
    affiliateId: getAffiliateId('pluralsight'),
    baseUrl: 'https://www.pluralsight.com',
    trackingParams: {},
    cookieDuration: 30,
    enabled: !!getAffiliateId('pluralsight'),
  },
  'linkedin-learning': {
    provider: 'linkedin-learning',
    affiliateId: getAffiliateId('linkedin-learning'),
    baseUrl: 'https://www.linkedin.com/learning',
    trackingParams: {},
    cookieDuration: 30,
    enabled: !!getAffiliateId('linkedin-learning'),
  },
  // Resume Services
  topresume: {
    provider: 'topresume',
    affiliateId: getAffiliateId('topresume'),
    baseUrl: 'https://www.topresume.com',
    trackingParams: {},
    enabled: !!getAffiliateId('topresume'),
  },
  'resume-io': {
    provider: 'resume-io',
    affiliateId: getAffiliateId('resume-io'),
    baseUrl: 'https://resume.io',
    trackingParams: {},
    cookieDuration: 30, // Impact Engagement click attribution window
    enabled: !!getAffiliateId('resume-io'),
  },
  zety: {
    provider: 'zety',
    affiliateId: getAffiliateId('zety'),
    baseUrl: 'https://zety.com',
    trackingParams: {},
    enabled: !!getAffiliateId('zety'),
  },
  'resume-genius': {
    provider: 'resume-genius',
    networkId: getNetworkId('shareasale'), // ResumeGenius via ShareASale
    baseUrl: 'https://www.resumegenius.com',
    trackingParams: {},
    enabled: !!getNetworkId('shareasale'),
  },
  // Career Resources
  amazon: {
    provider: 'amazon',
    affiliateId: getAffiliateId('amazon'),
    baseUrl: 'https://www.amazon.com',
    trackingParams: {},
    cookieDuration: 24,
    enabled: !!getAffiliateId('amazon'),
  },
};

/**
 * Generate affiliate URL with proper tracking parameters
 */
export const generateAffiliateUrl = (
  provider: AffiliateProvider,
  options: {
    occupation?: string;
    location?: string;
    type?: AffiliateType;
    searchQuery?: string;
    additionalParams?: Record<string, string>;
  } = {}
): string => {
  const config = affiliateConfigs[provider];
  if (!config || !config.enabled) {
    // Fallback to non-affiliate URL if not configured
    return generateFallbackUrl(provider, options);
  }

  const params = new URLSearchParams();

  // Add affiliate tracking
  if (config.affiliateId) {
    // Provider-specific affiliate parameter names
    const affiliateParams: Record<string, string> = {
      ziprecruiter: 'ref',
      indeed: 'ref',
      linkedin: 'ref',
      coursera: 'affiliate_id',
      udemy: 'affiliate_id',
      skillshare: 'ref',
      edx: 'ref',
      amazon: 'tag',
    };
    const paramName = affiliateParams[provider] || 'ref';
    params.append(paramName, config.affiliateId);
  }

  // Add network tracking (ShareASale, CJ, etc.)
  if (config.networkId) {
    if (provider === 'monster' || provider === 'careerbuilder' || provider === 'resume-genius') {
      // ShareASale format
      params.append('sssource', config.networkId);
    }
  }

  // Add UTM parameters for attribution
  params.append('utm_source', 'wageatlas');
  params.append('utm_medium', 'affiliate');
  params.append('utm_campaign', options.type || 'referral');
  if (options.occupation) {
    params.append('utm_content', `${provider}_${options.occupation.replace(/\s+/g, '_').toLowerCase()}`);
  }

  // Add search parameters for job boards
  if (options.type === 'job-search') {
    if (provider === 'ziprecruiter' || provider === 'indeed' || provider === 'simplyhired') {
      if (options.occupation) {
        params.append('q', options.occupation);
      }
      if (options.location) {
        params.append('l', options.location);
      }
    } else if (provider === 'linkedin') {
      if (options.occupation) {
        params.append('keywords', options.occupation);
      }
      if (options.location) {
        params.append('location', options.location);
      }
    } else if (provider === 'monster' || provider === 'careerbuilder') {
      if (options.occupation) {
        params.append('q', options.occupation);
      }
      if (options.location) {
        params.append('where', options.location);
      }
    }
  }

  // Add search parameters for education platforms
  if (options.type === 'training') {
    if (options.occupation || options.searchQuery) {
      const query = options.searchQuery || options.occupation || '';
      if (provider === 'coursera' || provider === 'udemy' || provider === 'edx') {
        params.append('query', query);
      } else if (provider === 'skillshare') {
        params.append('q', query);
      }
    }
  }

  // Add Amazon Associates tracking
  if (provider === 'amazon' && options.searchQuery) {
    params.append('k', options.searchQuery);
  }

  // Add any additional custom parameters
  if (options.additionalParams) {
    Object.entries(options.additionalParams).forEach(([key, value]) => {
      params.append(key, value);
    });
  }

  // Add config tracking params
  Object.entries(config.trackingParams).forEach(([key, value]) => {
    params.append(key, value);
  });

  return `${config.baseUrl}?${params.toString()}`;
};

/**
 * Generate fallback URL when affiliate is not configured
 */
const generateFallbackUrl = (
  provider: AffiliateProvider,
  options: {
    occupation?: string;
    location?: string;
    type?: AffiliateType;
    searchQuery?: string;
  } = {}
): string => {
  const baseUrls: Record<AffiliateProvider, string> = {
    ziprecruiter: 'https://www.ziprecruiter.com/jobs',
    indeed: 'https://www.indeed.com/jobs',
    linkedin: 'https://www.linkedin.com/jobs/search',
    monster: 'https://www.monster.com/jobs',
    glassdoor: 'https://www.glassdoor.com/Job',
    careerbuilder: 'https://www.careerbuilder.com/jobs',
    simplyhired: 'https://www.simplyhired.com/search',
    coursera: 'https://www.coursera.org/search',
    udemy: 'https://www.udemy.com/courses/search',
    skillshare: 'https://www.skillshare.com',
    edx: 'https://www.edx.org/search',
    pluralsight: 'https://www.pluralsight.com',
    'linkedin-learning': 'https://www.linkedin.com/learning',
    topresume: 'https://www.topresume.com',
    'resume-io': 'https://resume.io',
    zety: 'https://zety.com',
    'resume-genius': 'https://www.resumegenius.com',
    amazon: 'https://www.amazon.com',
  };

  const params = new URLSearchParams({
    utm_source: 'wageatlas',
    utm_medium: 'referral',
    utm_campaign: options.type || 'referral',
  });

  if (options.occupation) {
    params.append('utm_content', `${provider}_${options.occupation.replace(/\s+/g, '_').toLowerCase()}`);
  }

  // Add search parameters
  if (options.type === 'job-search') {
    if (options.occupation) {
      params.append('q', options.occupation);
    }
    if (options.location) {
      params.append('l', options.location);
    }
  }

  return `${baseUrls[provider]}?${params.toString()}`;
};

/**
 * Check if an affiliate provider is enabled
 */
export const isAffiliateEnabled = (provider: AffiliateProvider): boolean => {
  return affiliateConfigs[provider]?.enabled ?? false;
};

/**
 * Get CTA text for affiliate provider
 */
export const getAffiliateCtaText = (
  provider: AffiliateProvider,
  occupation: string = '',
  type: AffiliateType = 'job-search'
): string => {
  const occupationText = occupation ? `${occupation} ` : '';

  const texts: Record<AffiliateProvider, Record<AffiliateType, string>> = {
    ziprecruiter: {
      'job-search': `Find ${occupationText}Jobs`,
      training: `Find ${occupationText}Training`,
      resume: `Find ${occupationText}Jobs`,
      resource: `Find ${occupationText}Resources`,
    },
    indeed: {
      'job-search': `Search ${occupationText}Positions`,
      training: `Find ${occupationText}Training`,
      resume: `Search ${occupationText}Jobs`,
      resource: `Search ${occupationText}Resources`,
    },
    linkedin: {
      'job-search': `View ${occupationText}Network`,
      training: `Learn on LinkedIn`,
      resume: `View ${occupationText}Network`,
      resource: `View ${occupationText}Network`,
    },
    monster: {
      'job-search': `Find ${occupationText}Jobs on Monster`,
      training: `Find ${occupationText}Training`,
      resume: `Find ${occupationText}Jobs`,
      resource: `Find ${occupationText}Resources`,
    },
    glassdoor: {
      'job-search': `View ${occupationText}Jobs on Glassdoor`,
      training: `Find ${occupationText}Training`,
      resume: `View ${occupationText}Jobs`,
      resource: `View ${occupationText}Resources`,
    },
    careerbuilder: {
      'job-search': `Find ${occupationText}Jobs on CareerBuilder`,
      training: `Find ${occupationText}Training`,
      resume: `Find ${occupationText}Jobs`,
      resource: `Find ${occupationText}Resources`,
    },
    simplyhired: {
      'job-search': `Search ${occupationText}Jobs`,
      training: `Find ${occupationText}Training`,
      resume: `Search ${occupationText}Jobs`,
      resource: `Search ${occupationText}Resources`,
    },
    coursera: {
      'job-search': `Learn ${occupationText}Skills`,
      training: `Learn ${occupationText}Skills on Coursera`,
      resume: `Learn ${occupationText}Skills`,
      resource: `Learn ${occupationText}Skills`,
    },
    udemy: {
      'job-search': `Master ${occupationText}Training`,
      training: `Master ${occupationText}Training on Udemy`,
      resume: `Master ${occupationText}Training`,
      resource: `Master ${occupationText}Training`,
    },
    skillshare: {
      'job-search': `Learn ${occupationText}Skills`,
      training: `Learn ${occupationText}Skills on Skillshare`,
      resume: `Learn ${occupationText}Skills`,
      resource: `Learn ${occupationText}Skills`,
    },
    edx: {
      'job-search': `Learn ${occupationText}Skills`,
      training: `Learn ${occupationText}Skills on edX`,
      resume: `Learn ${occupationText}Skills`,
      resource: `Learn ${occupationText}Skills`,
    },
    pluralsight: {
      'job-search': `Learn ${occupationText}Skills`,
      training: `Learn ${occupationText}Skills on Pluralsight`,
      resume: `Learn ${occupationText}Skills`,
      resource: `Learn ${occupationText}Skills`,
    },
    'linkedin-learning': {
      'job-search': `Learn ${occupationText}Skills`,
      training: `Learn ${occupationText}Skills on LinkedIn Learning`,
      resume: `Learn ${occupationText}Skills`,
      resource: `Learn ${occupationText}Skills`,
    },
    topresume: {
      'job-search': `Get Professional Resume Help`,
      training: `Get Professional Resume Help`,
      resume: `Get Professional Resume Help from TopResume`,
      resource: `Get Professional Resume Help`,
    },
    'resume-io': {
      'job-search': `Create Your Resume`,
      training: `Create Your Resume`,
      resume: `Create Your Resume with Resume.io`,
      resource: `Create Your Resume`,
    },
    zety: {
      'job-search': `Build Your Resume`,
      training: `Build Your Resume`,
      resume: `Build Your Resume with Zety`,
      resource: `Build Your Resume`,
    },
    'resume-genius': {
      'job-search': `Create Your Resume`,
      training: `Create Your Resume`,
      resume: `Create Your Resume with ResumeGenius`,
      resource: `Create Your Resume`,
    },
    amazon: {
      'job-search': `Shop Career Books`,
      training: `Shop Career Books`,
      resume: `Shop Career Books`,
      resource: `Shop Career Books on Amazon`,
    },
  };

  return texts[provider]?.[type] || `Visit ${provider}`;
};

